import {
  DeductionApplicationType,
  enumUnit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { atom, Getter, Setter } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import {
  $itemsInterfaceEntries,
  $itemsInterfaceTicketId,
  $recalculateDeductionsTotal,
  DeductionChangeAction,
} from '../_components/_shared/ItemsInterface/_state';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedDeduction,
  NormalizedFreightCharge,
  NormalizedLineItem,
} from '../_components/_shared/ItemsInterface/_types';
import { parsePurchaseLoadLineItem } from '../_components/_shared/ItemsInterface/utils/parsePurchaseLoadLineItem';
import { purchaseLoadUpdateAtom } from '../_graphql/mutationPurchaseLoadLineItemsUpdate';
import {
  PurchaseLoadDeductionRecord,
  PurchaseLoadLineItemRecord,
} from '../types/purchaseLoad';
import { WeightUnitMode } from '../types/scaleTickets';
import { toMoney, toMoneyInput } from '../utils/money';
import { toWeightInput } from '../utils/weight';

import {
  $materialsTempToRealIdMapById,
  createMaterialsActions,
  FreightData,
  MutationLifecycle,
  resolveId,
} from './materialsForm.state';
import { mapWith } from './utils/mapAtomHelpers';

// ──────────────────────────────────────────────────────────────────────────────
// Inbound-specific: weight unit mode (separate localStorage key)
// ──────────────────────────────────────────────────────────────────────────────

export const $inboundWeightUnitMode = atomWithStorage<WeightUnitMode>(
  'scaleTicket.inbound.weightUnitMode',
  'scale',
);

// ──────────────────────────────────────────────────────────────────────────────
// Inbound-specific: normalization & payload building
// ──────────────────────────────────────────────────────────────────────────────

export const normalizeInboundEntries = (
  lineItems: PurchaseLoadLineItemRecord[],
  deductions: PurchaseLoadDeductionRecord[],
  freight?: FreightData | null,
): ItemsListEntry[] => {
  const normalizedLineItems: ItemsListEntry[] = lineItems.map((item) => ({
    ...parsePurchaseLoadLineItem.toNormalized(item),
    parentLineItemId: item.parentLineItemId ?? null,
  }));

  const normalizedDeductions: NormalizedDeduction[] = deductions.map((d) => ({
    kind: 'Deduction',
    id: d.id,
    deductionTypeId: d.deductionTypeId,
    deductionTypeName: d.deductionTypeName,
    applicationType: d.applicationType as 'Weight' | 'Price',
    weightValue: d.weightValue ?? null,
    priceValue: d.priceValue ?? null,
    calculatedAmount: d.calculatedAmount ?? null,
    pricingUnit: d.weightValue?.unit ?? enumUnit.Pounds,
    percentage: d.percentage ?? null,
    purchaseLoadLineItemId: d.purchaseLoadLineItemId,
    notes: d.notes,
  }));

  const hasFreight = freight?.freightCost || freight?.freightTerms;
  const normalizedFreight: NormalizedFreightCharge[] = hasFreight
    ? [
        {
          kind: 'FreightCharge',
          id: 'server-freight',
          freightCost: freight.freightCost ?? null,
          notes: freight.freightTerms ?? null,
        },
      ]
    : [];

  return [
    ...normalizedLineItems,
    ...normalizedDeductions,
    ...normalizedFreight,
  ];
};

const toLineItemPayload = (
  entry: NormalizedLineItem,
  tempToRealMap: Map<string, string>,
) => {
  const record = parsePurchaseLoadLineItem.fromNormalized(entry);
  const resolvedItemId = resolveId(entry.id, tempToRealMap);
  const resolvedParentId = entry.parentLineItemId
    ? (resolveId(entry.parentLineItemId, tempToRealMap) ?? null)
    : null;

  return {
    ...(resolvedItemId ? { id: resolvedItemId } : {}),
    type: entry.type,
    parentLineItemId: resolvedParentId,
    itemId: record.itemId,
    tagId: record.tagId,
    grossWeight: toWeightInput(record.grossWeight),
    tareWeight: toWeightInput(record.tareWeight),
    netWeight: toWeightInput(record.netWeight),
    unitPrice: record.unitPrice ? toMoneyInput(record.unitPrice) : null,
    pricingUnit: record.pricingUnit,
    settlementBasis: record.settlementBasis,
    requiresSorting: record.requiresSorting,
    notes: record.notes,
    vehicleVin: record.vehicleVin,
    vehicleYear: record.vehicleYear,
    vehicleMake: record.vehicleMake,
    vehicleModel: record.vehicleModel,
    vehicleTitleStatus: record.vehicleTitleStatus,
    catSerialNumber: record.catSerialNumber,
    catOemCode: record.catOemCode,
    catComplianceStatus: record.catComplianceStatus,
  };
};

const toDeductionPayload = (
  entry: NormalizedDeduction,
  tempToRealMap: Map<string, string>,
) => {
  const resolvedDeductionId = resolveId(entry.id, tempToRealMap);
  return {
    ...(resolvedDeductionId ? { id: resolvedDeductionId } : {}),
    deductionTypeId: entry.deductionTypeId,
    applicationType: entry.applicationType as DeductionApplicationType,
    weightValue: toWeightInput(entry.weightValue),
    priceValue: toMoneyInput(entry.priceValue),
    purchaseLoadLineItemId: entry.purchaseLoadLineItemId,
    notes: entry.notes,
  };
};

const buildInboundMutationPayload = (
  entries: ItemsListEntry[],
  tempToRealMap: Map<string, string>,
  weights?: { heavyWeight: Weight | null; lightWeight: Weight | null },
) => {
  const lineItems = entries.filter(
    (e): e is NormalizedLineItem => e.kind === enumLineItemKind.LineItem,
  );
  const deductions = entries.filter(
    (e): e is NormalizedDeduction => e.kind === enumLineItemKind.Deduction,
  );
  const freightEntry = entries.find(
    (e): e is NormalizedFreightCharge =>
      e.kind === enumLineItemKind.FreightCharge,
  );

  return {
    freightCost: freightEntry?.freightCost
      ? toMoneyInput(freightEntry.freightCost)
      : null,
    freightTerms: freightEntry?.notes ?? null,
    ...(lineItems.length > 0
      ? {
          lineItems: lineItems.map((entry) =>
            toLineItemPayload(entry, tempToRealMap),
          ),
        }
      : {}),
    ...(deductions.length > 0
      ? {
          deductions: deductions.map((entry) =>
            toDeductionPayload(entry, tempToRealMap),
          ),
        }
      : {}),
    ...(weights?.heavyWeight
      ? { heavyWeight: toWeightInput(weights.heavyWeight) }
      : {}),
    ...(weights?.lightWeight
      ? { lightWeight: toWeightInput(weights.lightWeight) }
      : {}),
  };
};

const setupInboundMutationAtom = (lifecycle: MutationLifecycle) => {
  const $mutation = purchaseLoadUpdateAtom({
    before: (get, set, { params }) => {
      lifecycle.onBefore(get, set, params.id);
    },
    onSuccess: (get, set, { params, result }) => {
      lifecycle.onSuccess(
        get,
        set,
        params.id,
        params.lineItems,
        params.deductions,
        {
          lineItems: result?.tradePurchaseLoadUpdate?.lineItems ?? [],
          deductions: result?.tradePurchaseLoadUpdate?.deductions ?? [],
        },
        params.failedToSaveMessage,
      );
    },
    onError: (get, set, { params }) => {
      lifecycle.onError(get, set, params.id, params.failedToSaveMessage);
    },
  });

  return atom(
    null,
    (
      _get: Getter,
      set: Setter,
      ctx: {
        ticketId: string;
        failedToSaveMessage: ((label: string) => string) | undefined;
        entries: ItemsListEntry[];
        tempToRealMap: Map<string, string>;
        weights:
          | { heavyWeight: Weight | null; lightWeight: Weight | null }
          | undefined;
      },
    ) => {
      set($mutation, {
        id: ctx.ticketId,
        failedToSaveMessage: ctx.failedToSaveMessage,
        ...buildInboundMutationPayload(
          ctx.entries,
          ctx.tempToRealMap,
          ctx.weights,
        ),
      });
    },
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Deduction-only mutation (separate from the main auto-save flow)
// ──────────────────────────────────────────────────────────────────────────────

const $deductionOnlyMutation = purchaseLoadUpdateAtom({
  onSuccess: (get, set, { params, result }) => {
    const ticketId = params.id;
    const returnedDeductions =
      result?.tradePurchaseLoadUpdate?.deductions ?? [];

    const currentState = get($itemsInterfaceEntries);
    const currentTicketId = get($itemsInterfaceTicketId);
    if (currentTicketId !== ticketId) return;

    const currentMap =
      get($materialsTempToRealIdMapById).get(ticketId) ??
      new Map<string, string>();
    const newMap = new Map(currentMap);

    const returnedById = new Map(returnedDeductions.map((d) => [d.id, d]));
    const consumedRealIds = new Set(newMap.values());

    const updatedItems = currentState.items.map((entry) => {
      if (entry.kind !== enumLineItemKind.Deduction) return entry;
      const deduction = entry as NormalizedDeduction;

      const resolvedId = resolveId(deduction.id, newMap);
      if (!resolvedId) {
        const match = returnedDeductions.find(
          (rd) =>
            rd.deductionTypeId === deduction.deductionTypeId &&
            rd.applicationType === deduction.applicationType &&
            !consumedRealIds.has(rd.id),
        );
        if (match) {
          newMap.set(deduction.id, match.id);
          consumedRealIds.add(match.id);
          return {
            ...deduction,
            calculatedAmount: match.calculatedAmount
              ? toMoney({ ...match.calculatedAmount })
              : null,
            percentage: match.percentage ?? null,
          };
        }
        return deduction;
      }

      const serverDeduction = returnedById.get(resolvedId);
      if (!serverDeduction) return deduction;

      return {
        ...deduction,
        calculatedAmount: serverDeduction.calculatedAmount
          ? toMoney({ ...serverDeduction.calculatedAmount })
          : null,
        percentage: serverDeduction.percentage ?? null,
      };
    });

    set($itemsInterfaceEntries, { ...currentState, items: updatedItems });

    if (newMap.size !== currentMap.size) {
      set($materialsTempToRealIdMapById, (prev) =>
        mapWith(prev, ticketId, newMap),
      );
    }

    set($recalculateDeductionsTotal);
  },
});

export const $inboundDeductionChange = atom(
  null,
  (
    get,
    set,
    _action: DeductionChangeAction,
    _deduction: NormalizedDeduction,
    allDeductions: NormalizedDeduction[],
  ) => {
    const ticketId = get($itemsInterfaceTicketId);
    if (!ticketId) return;

    const tempToRealMap =
      get($materialsTempToRealIdMapById).get(ticketId) ??
      new Map<string, string>();

    const deductionPayloads = allDeductions.map((d) =>
      toDeductionPayload(d, tempToRealMap),
    );

    set($deductionOnlyMutation, {
      id: ticketId,
      deductions: deductionPayloads,
    });
  },
);

// ──────────────────────────────────────────────────────────────────────────────
// Factory call
// ──────────────────────────────────────────────────────────────────────────────

const inboundActions = createMaterialsActions({
  debugPrefix: 'inbound',
  normalizeEntries: normalizeInboundEntries,
  setupMutationAtom: setupInboundMutationAtom,
});

// ──────────────────────────────────────────────────────────────────────────────
// Exports
// ──────────────────────────────────────────────────────────────────────────────

export const $saveInboundMaterials = inboundActions.$save;
export const $syncMaterialsForm = inboundActions.$sync;
